// src/components/property/ReviewImagesGallery.tsx
/**
 * ReviewImagesGallery Component
 *
 * Displays property images in Step 2 (Review Data) with:
 * - 4-column grid with 16:9 aspect ratio thumbnails
 * - Click to open lightbox with navigation
 * - Drag-and-drop reordering
 * - Delete functionality (hard delete)
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDndSensors } from '@/hooks/useDndSensors';
import { api } from '../../../convex/_generated/api';
import { X, GripVertical, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ImageLightbox } from './ImageLightbox';
import type { IReviewImagesGalleryProps, ISortableReviewImageProps } from '@/@types/property';

/**
 * Individual sortable image thumbnail
 */
function SortableReviewImage({
  id,
  storageId,
  index,
  onDelete,
  onClick,
}: ISortableReviewImageProps) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : ('auto' as const),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative group rounded-lg overflow-hidden bg-muted aspect-video',
        'border-2 border-transparent hover:border-primary transition-colors cursor-pointer'
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Property image ${index + 1}. Click to view full size.`}
    >
      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        className="appearance-none bg-black/50 border-0 absolute top-2 left-2 z-10 p-1 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Delete button */}
      <button
        onClick={e => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute top-2 right-2 z-10 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-all"
        aria-label="Delete image"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Index badge */}
      <div className="absolute bottom-2 left-2 z-10 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium">
        {index + 1}
      </div>

      {/* Image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Property image ${index + 1}`}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

/**
 * ReviewImagesGallery - Property images grid with drag-drop, delete, and lightbox
 */
function ReviewImagesGallery({ imageIds, propertyId }: IReviewImagesGalleryProps) {
  const sensors = useDndSensors();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const updateImageOrder = useMutation(api.property.mutations.updateImageOrder);
  const deleteImage = useMutation(api.property.mutations.deletePropertyImage);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = imageIds.findIndex(id => id.toString() === active.id);
      const newIndex = imageIds.findIndex(id => id.toString() === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newImageIds = arrayMove(imageIds, oldIndex, newIndex);
        updateImageOrder({ propertyId, newImageIds });
      }
    },
    [imageIds, propertyId, updateImageOrder]
  );

  const handleImageClick = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleDelete = useCallback(
    (index: number) => {
      deleteImage({ propertyId, imageIndex: index });
    },
    [propertyId, deleteImage]
  );

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  if (imageIds.length === 0) {
    return <p className="text-sm text-muted-foreground">No images were found for this property.</p>;
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={imageIds.map(id => id.toString())} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-4 gap-4">
            {imageIds.map((id, index) => (
              <SortableReviewImage
                key={id.toString()}
                id={id.toString()}
                storageId={id}
                index={index}
                onDelete={() => handleDelete(index)}
                onClick={() => handleImageClick(index)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <ImageLightbox
        imageIds={imageIds}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={handleLightboxClose}
      />
    </>
  );
}

export { ReviewImagesGallery };
